<script setup>
import { computed, inject } from 'vue'
import { RouterLink } from 'vue-router'
import { MENU_ITEM_STYLES, TRANSITION_DURATIONS } from '../constants/sidebar.constants'

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  level: {
    type: Number,
    default: 0,
  },
  collapsed: {
    type: Boolean,
    default: false,
  },
})

const expandedKeys = inject('expandedKeys')
const handleItemClick = inject('handleItemClick')
const isItemActive = inject('isItemActive')
const hasActiveChild = inject('hasActiveChild')
const searchQuery = inject('searchQuery')
const isFocused = inject('isFocused')
const isPinned = inject('isPinned')
const togglePin = inject('togglePin')
const recordVisit = inject('recordVisit')

const isExpanded = computed(() =>
  (searchQuery?.value?.trim() && props.item.hasChildren) || expandedKeys.value[props.item.key],
)

function highlightLabel(label) {
  const query = searchQuery?.value?.trim()
  if (!query)
    return [{ text: label, highlight: false }]

  const index = label.toLowerCase().indexOf(query.toLowerCase())
  if (index === -1)
    return [{ text: label, highlight: false }]

  return [
    { text: label.slice(0, index), highlight: false },
    { text: label.slice(index, index + query.length), highlight: true },
    { text: label.slice(index + query.length), highlight: false },
  ]
}

function onSubmenuBeforeEnter(element) {
  element.style.height = '0'
  element.style.opacity = '0'
  element.style.transform = 'translateY(-4px)'
}

function onSubmenuEnter(element) {
  const height = element.scrollHeight
  element.style.height = `${height}px`
  element.style.opacity = '1'
  element.style.transform = 'translateY(0)'
}

function onSubmenuAfterEnter(element) {
  element.style.height = 'auto'
}

function onSubmenuBeforeLeave(element) {
  element.style.height = `${element.scrollHeight}px`
  element.style.opacity = '1'
  element.style.transform = 'translateY(0)'
}

function onSubmenuLeave(element) {
  element.style.height = '0'
  element.style.opacity = '0'
  element.style.transform = 'translateY(-4px)'
}

const isExternalUrl = computed(() =>
  typeof props.item.route === 'string' && /^https?:\/\//.test(props.item.route),
)

const componentType = computed(() => {
  if (props.item.hasChildren)
    return 'button'

  if (props.item.route)
    return isExternalUrl.value ? 'a' : RouterLink

  return 'button'
})

const componentProps = computed(() => {
  if (props.item.route && !props.item.hasChildren) {
    if (isExternalUrl.value) {
      return {
        href: props.item.route,
        target: props.item.openInNewTab === false ? '_self' : '_blank',
        rel: 'noopener noreferrer',
      }
    }
    return { to: props.item.route }
  }

  return {}
})

function handleClick(item) {
  if (item.route && !item.hasChildren) {
    recordVisit?.(item)
    return
  }

  handleItemClick(item)
}
</script>

<template>
  <div class="">
    <component
      :is="componentType"
      v-bind="componentProps"
      v-tooltip.right="{
        value: collapsed && level === 0 ? item.label : undefined,
        class: 'font-hellix text-xs',
      }"
      class="group font-hellix flex items-center rounded-md text-sm transition-colors hover:bg-dss-surface-275 px-2.5 text-dss-secondary-650 cursor-pointer"
      :class="[
        item.breadcrumb ? 'h-auto py-1.5' : MENU_ITEM_STYLES.HEIGHT,
        {
          'bg-dss-surface-100': isItemActive(item),
          'bg-dss-surface-275/75': !isItemActive(item) && hasActiveChild(item),
          'ring-1 ring-inset ring-dss-secondary-650/40': isFocused?.(item),
          'font-light': level === 0 || level === 1 || level >= 2,
          'text-dss-surface-650': level >= 2,
          'ml-2': level === 2,
        },
        collapsed && level === 0 ? MENU_ITEM_STYLES.BUTTON_WIDTH : 'w-full px-3',
      ]"
      @click="handleClick(item)"
    >
      <div
        v-if="level === 0"
        :class="collapsed ? 'w-auto' : MENU_ITEM_STYLES.ICON_WIDTH"
        class="flex items-center shrink-0"
      >
        <i
          v-if="item.icon"
          :class="item.icon"
          class="text-sm transform-gpu will-change-transform"
        />
      </div>

      <span
        v-if="item.breadcrumb"
        class="flex-1 overflow-hidden flex flex-col justify-center transition-opacity duration-200"
        :class="collapsed && level === 0 ? 'hidden' : 'opacity-100'"
      >
        <span class="text-ellipsis whitespace-nowrap overflow-hidden text-left">
          <template
            v-for="part in highlightLabel(item.label)"
            :key="part.text + part.highlight"
          >
            <mark
              v-if="part.highlight"
              class="bg-transparent font-semibold text-dss-secondary-650 not-italic"
            >{{ part.text }}</mark>
            <template v-else>{{ part.text }}</template>
          </template>
        </span>
        <span class="text-[10px] text-dss-surface-600 truncate leading-tight">{{ item.breadcrumb.join(' › ') }}</span>
      </span>
      <span
        v-else
        class="flex-1 text-left overflow-hidden text-ellipsis whitespace-nowrap transition-opacity duration-200"
        :class="collapsed && level === 0 ? 'hidden' : 'opacity-100'"
      >
        <template
          v-for="part in highlightLabel(item.label)"
          :key="part.text + part.highlight"
        >
          <mark
            v-if="part.highlight"
            class="bg-transparent font-semibold text-dss-secondary-650 not-italic"
          >{{ part.text }}</mark>
          <template v-else>{{ part.text }}</template>
        </template>
      </span>

      <div
        class="flex items-center shrink-0 overflow-hidden"
        :class="[
          collapsed && level === 0 ? 'w-0 opacity-0' : 'w-6 opacity-100',
          !collapsed ? `transition-all ${TRANSITION_DURATIONS.CHEVRON}` : '',
        ]"
      >
        <i
          v-if="item.hasChildren"
          class="pi pi-chevron-down text-xs transition-transform ease-in-out"
          :class="[
            isExpanded ? 'rotate-180' : 'rotate-0',
            TRANSITION_DURATIONS.CHEVRON,
          ]"
        />
        <button
          v-else-if="!collapsed"
          v-tooltip.right="{
            value: isPinned?.(item) ? $t('menu.unpin') : $t('menu.pin'),
            class: 'font-hellix text-xs',
          }"
          type="button"
          class="flex items-center justify-center w-full transition-opacity"
          :class="isPinned?.(item) ? 'opacity-60 text-dss-secondary-650' : 'opacity-0 group-hover:opacity-40 text-dss-secondary-650 hover:opacity-60'"
          @click.stop.prevent="togglePin?.(item)"
        >
          <i
            class="text-xs"
            :class="isPinned?.(item) ? 'pi pi-bookmark-fill' : 'pi pi-bookmark'"
          />
        </button>
      </div>
    </component>

    <transition
      name="sidebar-submenu"
      appear
      @before-enter="onSubmenuBeforeEnter"
      @enter="onSubmenuEnter"
      @after-enter="onSubmenuAfterEnter"
      @before-leave="onSubmenuBeforeLeave"
      @leave="onSubmenuLeave"
    >
      <div
        v-if="item.hasChildren && isExpanded && item.children && !collapsed"
        class=""
        :class="[
          level === 0 ? 'ml-4 mt-1 border-l-2 border-dss-surface-200 pl-1.5 flex flex-col gap-1' : '',
          level === 1 ? 'ml-4 flex flex-col gap-1' : '',
        ]"
      >
        <menu-item
          v-for="child in item.children"
          :key="child.key"
          :item="child"
          :level="level + 1"
          :collapsed="collapsed"
        />
      </div>
    </transition>
  </div>
</template>

<style scoped>
.sidebar-submenu-enter-active,
.sidebar-submenu-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease, height 0.2s ease;
  overflow: hidden;
}

.sidebar-submenu-enter-from,
.sidebar-submenu-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.sidebar-submenu-enter-to,
.sidebar-submenu-leave-from {
  opacity: 1;
  transform: translateY(0);
}
</style>
